import { table as rules } from "@app/domain";
import { REACTIONS, type Reaction } from "@app/protocol";
import { useEffect, useState } from "react";
import { useT } from "../i18n/context";
import { send } from "../ws/singleton";
import { IconButton } from "./IconButton";
import { Icon } from "./icons";
import { useDismiss } from "./SoundControl";
import { EMOJI } from "./social";

/**
 * Quick messages and emoji for a seated player (review 0022): six words, eight emoji. After sending,
 * the button rests for the server's three seconds so a tap is never silently lost.
 */
export function ReactionControl({ size }: { size: number }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [resting, setResting] = useState(false);
  const root = useDismiss(open, () => setOpen(false));
  useEffect(() => {
    if (!resting) return;
    const id = window.setTimeout(() => setResting(false), rules.REACTION_GAP_MS);
    return () => window.clearTimeout(id);
  }, [resting]);
  const say = (reaction: Reaction) => {
    send({ type: "react", reaction });
    setOpen(false);
    setResting(true);
  };
  const words = REACTIONS.filter((r) => EMOJI[r] === undefined);
  const faces = REACTIONS.filter((r) => EMOJI[r] !== undefined);
  return (
    <div ref={root} className={`relative ${resting ? "opacity-50" : ""}`}>
      <IconButton
        label={t("reaction.open")}
        size={size}
        onClick={() => !resting && setOpen((was) => !was)}
      >
        <Icon name="smile" />
      </IconButton>
      {open ? (
        <div className="absolute left-0 top-[calc(100%+10px)] z-30 flex w-[280px] flex-col gap-2 rounded-[16px] border-[1.5px] border-edge bg-panel-strong p-3 shadow-panel backdrop-blur-[10px]">
          <div className="flex flex-wrap gap-1.5">
            {words.map((reaction) => (
              <button
                key={reaction}
                type="button"
                className="rounded-pill border-[1.5px] border-ink/30 bg-ink/6 px-3 py-1.5 text-[13px] font-bold hover:border-gold"
                onClick={() => say(reaction)}
              >
                {t(`reaction.${reaction}`)}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-8 gap-1">
            {faces.map((reaction) => (
              <button
                key={reaction}
                type="button"
                aria-label={t(`reaction.${reaction}`)}
                className="flex aspect-square items-center justify-center rounded-[10px] text-[22px] hover:bg-ink/10"
                onClick={() => say(reaction)}
              >
                <span aria-hidden="true">{EMOJI[reaction]}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
